import circle from '@turf/circle';
import { formattedGeoToStr, strToGeo } from '../../utils/geolocation';

export const roundToNearestInt = value => Math.round(value);

export const getBoundingBox = (element, pos) => {
  const width = element.offsetWidth;
  const height = element.offsetHeight;

  return {
    left: pos.x - width / 2,
    right: pos.x + width / 2,
    top: pos.y - height / 2,
    bottom: pos.y + height / 2,
  };
};

export const boundingBoxesOverlap = (bbox1, bbox2) => {
  return !(
    bbox1.right < bbox2.left ||
    bbox1.left > bbox2.right ||
    bbox1.bottom < bbox2.top ||
    bbox1.top > bbox2.bottom
  );
};

export const isOverlapping = (marker1, marker2) => {
  const element1 = marker1.marker.getElement();
  const element2 = marker2.marker.getElement();

  const pos1 = window.map.project(marker1.marker.getLngLat());
  const pos2 = window.map.project(marker2.marker.getLngLat());

  const roundedPos1 = {
    x: roundToNearestInt(pos1.x),
    y: roundToNearestInt(pos1.y),
  };
  const roundedPos2 = {
    x: roundToNearestInt(pos2.x),
    y: roundToNearestInt(pos2.y),
  };

  const bbox1 = getBoundingBox(element1, roundedPos1);
  const bbox2 = getBoundingBox(element2, roundedPos2);

  return boundingBoxesOverlap(bbox1, bbox2);
};

export const findOverlappingMarkers = iconsOnScreen => {
  const uniqueOverlappingIds = new Set();

  iconsOnScreen.forEach((marker1, index1) => {
    const overlapping = new Set();

    iconsOnScreen.forEach((marker2, index2) => {
      if (index1 !== index2 && isOverlapping(marker1, marker2)) {
        overlapping.add(marker2.id);
      }
    });

    if (overlapping.size > 0) {
      uniqueOverlappingIds.add(marker1.id);
      overlapping.forEach(id => uniqueOverlappingIds.add(id));
    }
  });

  return Array.from(uniqueOverlappingIds);
};

export const determineTopIconMarker = (
  currentTopIconMarker,
  overlappingIds,
) => {
  if (overlappingIds.length === 0) {
    return '';
  }

  const firstOverlappingId = overlappingIds[0];

  if (firstOverlappingId && currentTopIconMarker === '') {
    return firstOverlappingId;
  }

  return currentTopIconMarker;
};

export const handleOverlappingIcon = (
  iconsOnScreen,
  overlappingIds,
  topIcon,
) => {
  iconsOnScreen.forEach(item => {
    if (item.iconEl.children.props.isOverlapping && item.id === topIcon) {
      const newProps = {
        ...item.iconEl.children.props,
        isOverlapping: false,
        isTopIcon: true,
      };

      item.iconEl.children = {
        ...item.iconEl.children,
        props: newProps,
      };
    } else if (
      overlappingIds.includes(item.id) &&
      item.id !== topIcon &&
      !item.iconEl.children.props.isOverlapping
    ) {
      const newProps = {
        ...item.iconEl.children.props,
        isOverlapping: true,
        isTopIcon: false,
      };

      item.iconEl.children = {
        ...item.iconEl.children,
        props: newProps,
      };
    } else if (overlappingIds.length === 0) {
      const newProps = {
        ...item.iconEl.children.props,
        isOverlapping: false,
        isTopIcon: false,
      };

      item.iconEl.children = {
        ...item.iconEl.children,
        props: newProps,
      };
    }
  });
};

export const createAccuracyCircle = (map, id, center, radius) => {
  const circleGeoJSON = circle(center, radius, {
    steps: 50,
    units: 'meters',
  });

  const circleSourceId = `circle_${id}`;
  const circleLayerId = `circleLayer_${id}`;

  map.accuracyCirclesMap = {
    ...map.accuracyCirclesMap,
    [id]: true,
  };

  // this load function can be deffered by map.on('styledata', loadCircle)
  // so before mounting the circle we need to check if id is in accuracyCirclesMap
  // to make sure nothing has removed this source (e.g by global filtering it)
  const loadCircle = () => {
    const prevSource = map.getSource(circleSourceId);

    if (prevSource) {
      prevSource.setData(circleGeoJSON);
    } else if (id in map.accuracyCirclesMap) {
      map.addSource(circleSourceId, {
        type: 'geojson',
        data: circleGeoJSON,
      });
      map.addLayer({
        id: circleLayerId,
        type: 'fill',
        source: circleSourceId,
        paint: {
          'fill-color': 'rgba(31, 184, 20, 0.5)',
          'fill-opacity': 1,
        },
      });
    }
  };

  if (map.isStyleLoaded()) {
    loadCircle();
  } else {
    map.on('styledata', loadCircle);
  }
};

export const setCurrentStyle = () => {
  const currentStyle = localStorage.getItem('mapStyle');
  if (
    currentStyle &&
    (currentStyle === process.env.REACT_APP_MAP_STYLE_DEFAULT ||
      currentStyle === process.env.REACT_APP_MAP_STYLE_SATELLITE)
  ) {
    return currentStyle;
  }
  const defaultStyle = `${process.env.REACT_APP_MAP_STYLE_DEFAULT}`;
  localStorage.setItem('mapStyle', defaultStyle);
  return defaultStyle;
};

export const changeOrientation = () => {
  const newPintch = window.map?.getPitch() === 0 ? 60 : 0;
  window.map.flyTo({
    pitch: newPintch,
  });
};

export const geoToUrl = (coordinates, zoom) =>
  coordinates && coordinates.length >= 2
    ? formattedGeoToStr(coordinates[0], coordinates[1], zoom)
    : null;

export const parseGeoUrlLocation = location => {
  if (!location) return;

  // strToGeo should still split your URL-string into { longitude, latitude, zoom } (all strings)
  const { longitude, latitude, zoom } = strToGeo(location);

  // 1) Turn them into real Numbers
  const lon = Number.parseFloat(longitude);
  const lat = Number.parseFloat(latitude);
  const z = Number.parseFloat(zoom);

  // 2) Quick sanity: are they finite?
  if (!Number.isFinite(lon) || !Number.isFinite(lat) || !Number.isFinite(z)) {
    return; // not even a valid number
  }

  // 3) Range‐check:
  const lonValid = lon >= -180 && lon <= 180;
  const latValid = lat >= -90 && lat <= 90;
  // (tweak zoom‐range to your mapping library’s limits)
  const zoomValid = z >= 0 && z <= 22;

  if (lonValid && latValid && zoomValid) {
    return {
      center: [lon, lat],
      zoom: z,
    };
  }
};

export const handleBackButton = (location, isMapPage) => {
  if (isMapPage) {
    const geoUrl = location.pathname.split('/portal/maps/')[1];

    const { center, zoom } = parseGeoUrlLocation(geoUrl) || {};

    if (center && zoom) {
      window.map.flyTo({
        center,
        zoom,
      });
    }
  }
};
